import {
  ExcelExportResponseDto,
  ExcelVisibleColumns,
  OptionBasicDtoOptionType,
  OptionTextDto,
  WorkflowStatusDtoType,
} from "../../services/Client";
import { IVirtualizedTableColumn } from "../../components/Table/VirtualizedTableInterfaces";
import { EVirtualizedTableCellType } from "../../components/Table/VirtualizedTableEnums";
import { LanguageCodeEnum } from "../AdminView/LanguageTab/LanguageTable";
import DataAdapterOptionsView from "./DataAdapterOptionsView";
import { FlagNameValues } from "semantic-ui-react";

export enum EColumnKey {
  DerivateKey = "derivateStates",
  OptionBasicViewKey = "optionBasicView",
  ConstraintKey = "constraint",
  ExclusionKey = "exclusion",
  PackageContentKey = "packageContent",
  ObjectIDKey = "objectId",
  StatusViewKey = "statusView",
  ShorttextKey = "shorttext",
  LongtextKey = "longtext",
  FooterKey = "footer",
  UspKey = "usp",
  OptionTypeKey = "optionType",
  FlagKey = "flags",
  ChangedByKey = "changedBy",
  ChangedTimestampKey = "changedTimestamp",
  GenerationKey = "generation",
}

export enum EActiveMarketKey {
  BasicMarketActiveKey = 0,
  SpecificMarketActiveKey = 1,
}

export enum PropertiesKey {
  CnrKey = "cnr",
  SourceCnrKey = "sourceCnr",
  TargetCnrKey = "targetCnr",
  BasicMarketCnrKey = "CXX",
}

export const MissingPDiPAMTBsKey = "missingPDiPAMTBs";

export enum SpecificFilterEnum {
  SHOW_ALL = "",
  SHOW_PDIP_OPTIONS = "SHOW_PDIP_OPTIONS",
  SHOW_BM_OPTIONS = "SHOW_BM_OPTIONS",
  SHOW_PDIP_SYNC_ERROR_OPTIONS = "SHOW_PDIP_SYNC_ERROR_OPTIONS",
}

export const PDiPExcludedOptionTypes = new Set<OptionBasicDtoOptionType>([
  OptionBasicDtoOptionType.SER,
  OptionBasicDtoOptionType.Z,
  OptionBasicDtoOptionType.CT,
  OptionBasicDtoOptionType.PKG,
]);

export class OptionViewUtils {
  /**
   * Returns the RowType using the key
   * @param key
   */
  getRowType(key: string): EVirtualizedTableCellType {
    let returnValue: EVirtualizedTableCellType;
    switch (key) {
      case "derivateStates":
        returnValue = EVirtualizedTableCellType.DERIVATE;
        break;
      case "optionBasicView":
        returnValue = EVirtualizedTableCellType.TEXTINPUT;
        break;
      case "availableEnd":
        returnValue = EVirtualizedTableCellType.DATE;
        break;
      case "availableStart":
        returnValue = EVirtualizedTableCellType.DATE;
        break;
      case "categoryText":
        returnValue = EVirtualizedTableCellType.TEXTINPUT;
        break;
      case "comment":
        returnValue = EVirtualizedTableCellType.TEXTAREA;
        break;
      case "constraint":
        returnValue = EVirtualizedTableCellType.ONCLICKINPUT;
        break;
      case "price":
        returnValue = EVirtualizedTableCellType.PRICE;
        break;
      //
      case "optionTypeView":
        returnValue = EVirtualizedTableCellType.TEXTINPUT;
        break;
      case "statusView":
        returnValue = EVirtualizedTableCellType.WORKFLOWSTATE;
        break;
      case "shorttext":
        returnValue = EVirtualizedTableCellType.TWOLANGUAGECOMPAREINPUT;
        break;
      case "longtext":
        returnValue = EVirtualizedTableCellType.TWOLANGUAGECOMPARETEXTAREA;
        break;
      case "footer":
        returnValue = EVirtualizedTableCellType.TWOLANGUAGETEXTAREA;
        break;
      case "usp":
        returnValue = EVirtualizedTableCellType.TWOLANGUAGETEXTAREA;
        break;
      case "exclusion":
        returnValue = EVirtualizedTableCellType.ONCLICKINPUT;
        break;
      case "packageContent":
        returnValue = EVirtualizedTableCellType.ONCLICKINPUT;
        break;
      case "flags":
        returnValue = EVirtualizedTableCellType.FLAG;
        break;
      case "optionIndicators":
        returnValue = EVirtualizedTableCellType.OPTION_INDICATOR;
        break;
      case "reference":
        returnValue = EVirtualizedTableCellType.TEXTAREA;
        break;
      case "referenceExc":
        returnValue = EVirtualizedTableCellType.TEXTAREA;
        break;
      case "intSequence":
        returnValue = EVirtualizedTableCellType.NUMBERINPUT;
        break;
      case "changedBy":
        returnValue = EVirtualizedTableCellType.TEXTINPUT;
        break;
      case "changedTimestamp":
        returnValue = EVirtualizedTableCellType.TEXTINPUT;
        break;
      default:
        returnValue = EVirtualizedTableCellType.DEFAULTINPUT;
        break;
    }
    return returnValue;
  }

  /**
   * Decides on the basis of the key
   * whether the Row is visible at the start
   * @param key
   */
  isRowVisible(key: string): boolean {
    let isVisible: boolean;
    switch (key) {
      case "optionBasicView":
        isVisible = true;
        break;
      case "optionTypeView":
        isVisible = true;
        break;
      case "derivateStates":
        isVisible = true;
        break;
      case "shorttext":
        isVisible = true;
        break;
      case "longtext":
        isVisible = true;
        break;
      case "footer":
        isVisible = true;
        break;
      case "usp":
        isVisible = true;
        break;
      case "intSequence":
        isVisible = true;
        break;
      case "changedBy":
        isVisible = true;
        break;
      case "changedTimestamp":
        isVisible = true;
        break;
      default:
        isVisible = false;
        break;
    }
    return isVisible;
  }

  isRowReadOnly(key: string, propReadOnly: boolean): boolean {
    let isReadOnly: boolean;
    switch (key) {
      case "optionBasicView":
        isReadOnly = true;
        break;
      case "optionTypeView":
        isReadOnly = true;
        break;
      case "categoryText":
        isReadOnly = true;
        break;
      case "changedBy":
        isReadOnly = true;
        break;
      case "changedTimestamp":
        isReadOnly = true;
        break;
      case "intSequence":
        isReadOnly = true;
        break;
      default:
        isReadOnly = propReadOnly;
        break;
    }
    return isReadOnly;
  }

  isRowSortable(key: string): boolean {
    let sortable: boolean;
    switch (key) {
      case "derivateStates":
        sortable = false;
        break;
      case "shorttext":
        sortable = false;
        break;
      case "longtext":
        sortable = false;
        break;
      case "footer":
        sortable = false;
        break;
      case "usp":
        sortable = false;
        break;
      case "constraint":
        sortable = false;
        break;
      case "exclusion":
        sortable = false;
        break;
      case "packageContent":
        sortable = false;
        break;
      // TODO to be fixed (all the values are null)
      case "price":
        sortable = false;
        break;
      case "flags":
        sortable = false;
        break;
      case "optionIndicators":
        sortable = false;
        break;
      case "changedBy":
        sortable = false;
        break;
      default:
        sortable = true;
        break;
    }
    return sortable;
  }

  /**
   * Returns the column default width using the key
   * @param key
   */
  getColumnDefaultWidth(key: string): number {
    let returnValue: number;
    switch (key) {
      case "optionBasicView":
        returnValue = 110;
        break;
      case "availableEnd":
        returnValue = 130;
        break;
      case "availableStart":
        returnValue = 130;
        break;
      case "categoryText":
        returnValue = 150;
        break;
      case "comment":
        returnValue = 150;
        break;
      case "constraint":
        returnValue = 250;
        break;
      case "price":
        returnValue = 100;
        break;
      //
      case "optionTypeView":
        returnValue = 180;
        break;
      case "statusView":
        returnValue = 100;
        break;
      case "shorttext":
        returnValue = 525;
        break;
      case "longtext":
        returnValue = 500;
        break;
      case "footer":
        returnValue = 200;
        break;
      case "usp":
        returnValue = 200;
        break;
      case "exclusion":
        returnValue = 250;
        break;
      case "packageContent":
        returnValue = 250;
        break;
      case "optionIndicators":
        returnValue = 90;
        break;
      case "reference":
        returnValue = 120;
        break;
      case "referenceExc":
        returnValue = 130;
        break;
      case "intSequence":
        returnValue = 100;
        break;
      case "flags":
        returnValue = 250;
        break;
      case "changedBy":
        returnValue = 200;
        break;
      case "changedTimestamp":
        returnValue = 200;
        break;
      default:
        returnValue = 100;
        break;
    }
    return returnValue;
  }
}

const mapRowKeyToExcelColumn = (
  key: string | undefined
): ExcelVisibleColumns | undefined => {
  let excelColumn = undefined;
  switch (key) {
    case "optionTypeView":
      excelColumn = ExcelVisibleColumns.OPTION_TYPE;
      break;
    case "statusView":
      excelColumn = ExcelVisibleColumns.WORKFLOW;
      break;
    case "comment":
      excelColumn = ExcelVisibleColumns.COMMENT;
      break;
    case "availableStart":
      excelColumn = ExcelVisibleColumns.AVAILABLE_START;
      break;
    case "availableEnd":
      excelColumn = ExcelVisibleColumns.AVAILABLE_END;
      break;
    case "constraint":
      excelColumn = ExcelVisibleColumns.CONSTRAINT;
      break;
    case "exclusion":
      excelColumn = ExcelVisibleColumns.EXCLUSION;
      break;
    case "packageContent":
      excelColumn = ExcelVisibleColumns.PACKAGE_CONTENT;
      break;
    case "reference":
      excelColumn = ExcelVisibleColumns.REFERENCE;
      break;
    case "referenceExc":
      excelColumn = ExcelVisibleColumns.REFERENCE_EXC;
      break;
    case "price":
      excelColumn = ExcelVisibleColumns.PRICE;
      break;
    case "intSequence":
      excelColumn = ExcelVisibleColumns.SEQUENCE;
      break;
    case "flags":
      excelColumn = ExcelVisibleColumns.FLAGS;
      break;
    case "pdiptext":
      excelColumn = ExcelVisibleColumns.PDIP_TEXT;
      break;
    default:
      break;
  }

  return excelColumn;
};

export const mapToExcelColumns = (
  optionColums: IVirtualizedTableColumn[]
): ExcelVisibleColumns[] => {
  let excelColumns: ExcelVisibleColumns[] = [];

  optionColums.forEach((a) => {
    const col = mapRowKeyToExcelColumn(a.key);
    if (col !== undefined) {
      excelColumns.push(col);
    }
  });

  return excelColumns;
};

export const base64toBlob = (base64Data: any, contentType: any): Blob => {
  contentType = contentType || "";
  const sliceSize = 1024;
  const byteCharacters = atob(base64Data);
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  let byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);

    let bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
};
export const downloadExcel = (excelLink: ExcelExportResponseDto) => {
  if (excelLink !== undefined && excelLink.fileName !== undefined) {
    if (excelLink.filePath) {
      const link = document.createElement("a");
      link.href = excelLink.filePath;
      link.setAttribute("download", excelLink.fileName); // Set desired file name here
      // Append the link to the body
      document.body.appendChild(link);
      // Click the link to trigger the download
      link.click();
      if (link && link.parentNode) {
        // Clean up by removing the link
        link.parentNode.removeChild(link);
      }
    }
  }
};

/** Returns true if the following conditions are fulfilled:
 *  1. Both short texts are either empty or have non empty values
 *  2. Both long texts are either empty or have non empty values
 */
export const validateTexts = (texts: OptionTextDto[]): boolean => {
  let emptyShorttextDE: boolean | undefined;
  let emptyShorttextEN: boolean | undefined;
  let emptyLongtextDE: boolean | undefined;
  let emptyLongtextEN: boolean | undefined;

  for (let txt of texts) {
    if (txt.language) {
      if (txt.language.code === LanguageCodeEnum.BASIC_GERMAN.toString()) {
        if (txt.shorttext !== undefined && txt.shorttext !== null) {
          emptyShorttextDE = txt.shorttext.trim().length === 0;
        }
        if (txt.longtext !== undefined && txt.longtext !== null) {
          emptyLongtextDE = txt.longtext.trim().length === 0;
        }
      } else if (
        txt.language.code === LanguageCodeEnum.BASIC_ENGLISH.toString()
      ) {
        if (txt.shorttext !== undefined && txt.shorttext !== null) {
          emptyShorttextEN = txt.shorttext.trim().length === 0;
        }
        if (txt.longtext !== undefined && txt.longtext !== null) {
          emptyLongtextEN = txt.longtext.trim().length === 0;
        }
      }
    }
  }

  return (
    ((emptyShorttextDE && emptyShorttextEN) ||
      (emptyShorttextDE === false && emptyShorttextEN === false)) &&
    ((emptyLongtextDE && emptyLongtextEN) ||
      (emptyLongtextDE === false && emptyLongtextEN === false))
  );
};

export const extractMultiFields = (
  item: DataAdapterOptionsView,
  propKey: keyof DataAdapterOptionsView
): {} => {
  return (item[propKey] as any[]).map((value: any) => {
    return {
      flag: value.flag as FlagNameValues,
      value: value.text,
      originalValues: value,
      isMaintenanceStep: item.statusView === WorkflowStatusDtoType.MAINTENANCE,
      isSpecificOption:
        item.cnr && item.cnr.cnr !== PropertiesKey.BasicMarketCnrKey,
    };
  });
};

export const newGridIntialColumns = [
  "categoryText",
  "optionCode",
  "intSequence",
  "derivateStates",
  "pdiptext",
  "shorttext",
  "longtext",
  "footer",
  "usp",
  "statusView",
  "comment",
];



// WEBPACK FOOTER //
// ./src/views/OptionsView/OptionsViewUtils.ts